import { Box, Button, Step, StepLabel, Stepper, Typography } from "@mui/material";
import DropdownSearch from "../../../views/dropdown-search/DropdownSearch";
import classes from './CreateVariationModal.module.scss';
import { useCallback, useEffect, useMemo, useState } from "react";
import { CreateVariationModalProps } from "../../../interfaces/props/CreateVariationModalProps";
import { FilterOption } from "../../../interfaces/FilterOption";
import { VariationPreferences } from '../../../enums/VariationPreferences';
import RadioButtons from '../../../views/radio-buttons/RadioButtons';
import { CustomizedVariation } from '../../../interfaces/CustomizedVariation';
import StringHelper from '../../../helpers/string.helper';
import { Structure, VariationHeadline, VariationLength, VariationType } from '../../../enums/VariationCustomization';
import { useErrorBoundary } from 'react-error-boundary';
import LanguagesService from '../../../services/languages.service';
import SitesService from '../../../services/sites.service';
import { Link } from 'react-router-dom';
import { Temperature } from '../../../enums/Temperature';
import NameHelper from '../../../helpers/name.helper';
import { Relations } from '../../../enums/Relations';
import OpenAIService from '../../../services/openai.service';

const STEPS: string[] = ['Language & Site', 'Customize Variation'];
const variationType: VariationType[] = [VariationType.DIRECT_TRANSLATION, VariationType.REWRITE_REPHRASE];
const variationLength: VariationLength[] = [VariationLength.FULL_PIECE, VariationLength.SUMMARY];
const variationTitle: VariationHeadline[] = [VariationHeadline.REGULAR, VariationHeadline.CLICKBAIT];
const structures: Structure[] = Object.values(Structure);

const CreateVariationModal = ({ site, language, customizeVariation, onSiteChange, onLanguageChange, onCustomizeChange, onCreate, onDiscard, variations }: CreateVariationModalProps) => {
  const [languages, setLanguages] = useState<FilterOption[]>([]);
  const [sites, setSites] = useState<FilterOption[]>([]);
  const [activeStep, setActiveStep] = useState<number>(1);
  const [selectedVariationLength, setSelectedVariationLength] = useState<string>('');
  const [isDuplicated, setIsDuplicated] = useState<boolean>(false);
  const [selectedStructure, setSelectedStructure] = useState<string>(Structure.HTML_WITH_BLOCKS);
  const [isOpenAIDown, setIsOpenAIDown] = useState<boolean>(false);
  const { showBoundary } = useErrorBoundary();

  useEffect(() => {
    OpenAIService.isOpenAIDown()
      .then((status) => setIsOpenAIDown(status))
      .catch((error) => console.error(error))
  }, [])

  const isCreateButtonDisabled: boolean = useMemo(() => {
    if (VariationType.REWRITE_REPHRASE === customizeVariation.rephraseOrTranslate) {
      return 0 === selectedVariationLength.length
    }

    return false;
  }, [customizeVariation, selectedVariationLength]);

  const isNextButtonDisabled: boolean = useMemo(() => {
    return null === site || null === language;
  }, [site, language]);

  const isDiscardButtonDisabled: boolean = useMemo(() => {
    return null === site && null === language;
  }, [site, language]);

  const createUpdatedCustomization = useCallback((setName: string, value: string) => {
    const commonUpdates = {
      ...customizeVariation,
      [setName]: value,
    };

    switch (value) {
      case VariationLength.SUMMARY:
        setSelectedVariationLength(value);
        setSelectedStructure(Structure.PLAIN_TEXT);

        return {
          ...commonUpdates,
          [VariationPreferences.KEEP_WP_BLOCKS]: false,
        };
      case VariationLength.FULL_PIECE:
        setSelectedVariationLength(value);

        return commonUpdates;
      case VariationType.DIRECT_TRANSLATION:
        setSelectedVariationLength('');
        delete commonUpdates[VariationPreferences.TEXT_LENGTH];

        return {
          ...commonUpdates,
          [VariationPreferences.TEMPERATURE]: isDuplicated ? Temperature.DUPLICATE_TRANSLATION : Temperature.DIRECT_TRANSLATION,
        };
      case VariationType.REWRITE_REPHRASE:
        return {
          ...commonUpdates,
          [VariationPreferences.TEMPERATURE]: Temperature.REPHRASE,
        };
      case Structure.HTML_WITH_BLOCKS:
        setSelectedStructure(value);
        return {
          ...commonUpdates,
          [VariationPreferences.STRUCTURE]: Structure.HTML_WITH_BLOCKS,
        };
      case Structure.HTML_WITHOUT_BLOCKS:
        setSelectedStructure(value);
        return {
          ...commonUpdates,
          [VariationPreferences.STRUCTURE]: Structure.HTML_WITHOUT_BLOCKS,
        };
      case Structure.PLAIN_TEXT:
        setSelectedStructure(value);
        return {
          ...commonUpdates,
          [VariationPreferences.STRUCTURE]: Structure.PLAIN_TEXT,
        };
      default:
        return commonUpdates;
    }
  }, [customizeVariation, setSelectedVariationLength, setSelectedStructure, isDuplicated]);


  const RadioChangeHandler = useCallback((setName: string, value: string) => {
    const updatedCustomizeVariation = createUpdatedCustomization(setName, value);
    onCustomizeChange(updatedCustomizeVariation);
  }, [onCustomizeChange, createUpdatedCustomization]);

  const handleNext = () => {
    setActiveStep((previousActiveStep) => previousActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((previousActiveStep) => previousActiveStep - 1);
    const updatedCustomizeVariation: CustomizedVariation = {
      ...customizeVariation,
      [VariationPreferences.REPHRASE_OR_TRANSLATE]: VariationType.DIRECT_TRANSLATION,
      [VariationPreferences.HEADLINE]: VariationHeadline.REGULAR,
      [VariationPreferences.TEMPERATURE]: isDuplicated ? Temperature.DUPLICATE_TRANSLATION : Temperature.DIRECT_TRANSLATION,
    };
    delete updatedCustomizeVariation[VariationPreferences.TEXT_LENGTH];
    setSelectedVariationLength('');
    setSelectedStructure(Structure.HTML_WITH_BLOCKS);
    onCustomizeChange(updatedCustomizeVariation);
  };

  const onSiteValueChangeHandler = useCallback((event: Event, value: FilterOption | null) => {
    onSiteChange(value);
  }, [onSiteChange]);

  const onLanguageValueChangeHandler = useCallback((event: Event, value: FilterOption | null) => {
    onLanguageChange(value);
  }, [onLanguageChange]);

  const onInputChangeLanguage = async (event: Event, inputValue: string) => {
    try {
      const languages = await LanguagesService.getAllByParams('name', inputValue);
      setLanguages(languages);
    } catch (err) {
      showBoundary(err);
    }
  };

  const onInputChangeSite = async (event: Event, inputValue: string) => {
    try {
      const sites = await SitesService.getAllByParams('name', inputValue)
      setSites(sites);
    } catch (err) {
      showBoundary(err);
    }
  };

  const checkDuplicate = useMemo(() => {
    if (variations && language) {
      const matchingVariation = variations.find(variation =>
        NameHelper.getNameFromEntity(variation, Relations.LANGUAGE) === language.name &&
        VariationType.DIRECT_TRANSLATION === variation.attributes.type);
      if (matchingVariation) {
        setIsDuplicated(true);
        const variationId = matchingVariation.id;
        return <Box className={`${classes['duplicate-message-box']} p-2 p-sm-3 mb-2`}>
          <Typography>There's an existing translation of this article in your chosen language. </Typography>
          <Typography>You can take a look
            <Link
              className='ms-1'
              to={`../variation/${variationId}`}
              style={{ color: 'white', fontWeight: 'bold' }}
            >
              here
            </Link>
          </Typography>
        </Box>;
      }

      setIsDuplicated(false);
    }
  }, [variations, language]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [languages, sites] = await Promise.all([
          LanguagesService.getAllByParams(),
          SitesService.getAllByParams(),
        ]);
        setLanguages(languages);
        setSites(sites);
      } catch (err) {
        showBoundary(err);
      }
    };

    fetchInitialData();
  }, [showBoundary]);



  const renderSiteAndLanguage = () => {
    return (
      <Box className='h-100 d-flex flex-column justify-content-between'>
        <Box>
          <Typography className='text-subtitle mt-4 d-flex justify-content-center align-items-center'>
            To create New Variation of this article, please choose the Site and Language and click on "Next" button
          </Typography>
          <Box className={`${classes['dropdowns-box']} d-flex w-100 my-1}`} >
            <Box className={`d-flex justify-content-sm-center mt-3 ${classes.filterWidth}`}>
              <DropdownSearch options={languages} value={language} setValue={onLanguageValueChangeHandler} onInputChange={onInputChangeLanguage} placeholder={'Search your language here'} />
            </Box>
            <Box className={`d-flex justify-content-sm-center mt-3  ${classes.filterWidth}`}>
              <DropdownSearch options={sites} value={site} setValue={onSiteValueChangeHandler} onInputChange={onInputChangeSite} placeholder={'Search your site here'} />
            </Box>
          </Box>

        </Box>
        <Box className='d-flex flex-column'>
          <Box>
            {checkDuplicate}
          </Box>
          <Box>
            {isOpenAIDown ?
              (<Box className={`${classes['openai-message-box']} p-2 d-flex justify-content-center align-items-center my-2`}>
                OpenAI is currently unavailable
              </Box>) :
              <></>
            }
          </Box>
          <Box className='d-flex justify-content-between'>
            <Button
              className={`${isDiscardButtonDisabled ? classes.disabledCreateButton : classes.button} w-50`}
              disabled={isDiscardButtonDisabled}
              onClick={onDiscard}
            >
              Discard
            </Button>
            <Button
              className={`${isNextButtonDisabled || isOpenAIDown ? classes.disabledCreateButton : classes.enabledCreateButton} w-50 ms-3`}
              disabled={isNextButtonDisabled || isOpenAIDown}
              onClick={handleNext}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderRadioButtons = () => {
    return (
      <Box className='d-flex flex-column justify-content-between vh-100 pt-4'>
        <Box className='d-flex flex-column justify-content-around h-100'>
          <RadioButtons
            label='Variation Type'
            options={variationType}
            onRadioChange={(value) => RadioChangeHandler(VariationPreferences.REPHRASE_OR_TRANSLATE, value)}
            defaultValue={VariationType.DIRECT_TRANSLATION}
          />
          <RadioButtons
            label='Variation Length'
            options={variationLength}
            onRadioChange={(value) => RadioChangeHandler(VariationPreferences.TEXT_LENGTH, value)}
            value={selectedVariationLength}
            disabled={VariationType.DIRECT_TRANSLATION === customizeVariation.rephraseOrTranslate}
          />
          <RadioButtons
            label='Variation Structure'
            options={structures}
            onRadioChange={(value) => RadioChangeHandler(VariationPreferences.STRUCTURE, value)}
            disabled={VariationLength.SUMMARY === customizeVariation.textLength}
            value={selectedStructure}
            defaultValue={Structure.HTML_WITH_BLOCKS}
          />
          <RadioButtons
            label='Variation title'
            options={variationTitle}
            onRadioChange={(value) => RadioChangeHandler(VariationPreferences.HEADLINE, value)}
            defaultValue={VariationHeadline.REGULAR}
          />
        </Box>
        <Box className='d-flex justify-content-between mt-5'>
          <Button
            className={`${classes.button} w-50`}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            className={`${isCreateButtonDisabled ? classes.disabledCreateButton : classes.enabledCreateButton} w-50 ms-3`}
            disabled={isCreateButtonDisabled}
            onClick={onCreate}
          >
            Create New Variation
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box className={`empty-modal ${classes.variationModal} d-flex flex-column p-4`}>
      <Typography className='h3 d-flex aligh-items-center justify-content-center'>Create New Variation</Typography>
      <Stepper activeStep={activeStep - 1} alternativeLabel className='pt-3'>
        {STEPS.map((label) => (
          <Step key={StringHelper.generateRandomString(10)}>
            <StepLabel StepIconProps={{ classes: { root: classes['dateStep'] } }}>
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      {activeStep === 1 ? renderSiteAndLanguage() : renderRadioButtons()}
    </Box>
  );
};

export default CreateVariationModal;
